import { router } from "@inertiajs/react";
import { useRef, useState } from "react";

import backgroundRed from "@/assets/background_red.svg";
import AuthCard from "@/components/AuthCard";
import Button from "@/components/Button";
import CourseSummaryCard from "@/components/CourseSummaryCard";
import { confirmForStudentEnrollment, enrollments, waitLists } from "@/routes";
import type { Course, Enrollment, WaitList } from "@/types";

type Props = {
  data: Enrollment | WaitList;
  course: Course;
  /** Cancelling a waitlist spot instead of an enrollment. */
  waitlist?: boolean;
  token?: string;
};

export default function CancelForStudent({
  data: record,
  course,
  waitlist = false,
  token,
}: Props) {
  const [processing, setProcessing] = useState(false);
  // `disabled` only takes effect after a re-render, so a rapid double-click
  // can still fire two requests; this ref blocks the second one synchronously.
  const submittingRef = useRef(false);

  function cancel() {
    if (submittingRef.current) return;
    submittingRef.current = true;

    const options = token ? { query: { token } } : undefined;
    const url = waitlist
      ? waitLists.destroy(record.id).url
      : enrollments.destroy(record.id, options).url;
    router.delete(url, {
      onStart: () => setProcessing(true),
      onFinish: () => {
        submittingRef.current = false;
        setProcessing(false);
      },
    });
  }

  function goToConfirm() {
    const options = token ? { query: { token } } : undefined;
    router.get(confirmForStudentEnrollment(record.id, options).url);
  }

  return (
    <AuthCard
      title="Anula tu inscripción"
      subtitle={
        waitlist
          ? "Estás en la lista de espera de este curso. Al anular, perderás tu lugar en la lista."
          : undefined
      }
      background={backgroundRed}
    >
      <div className="flex flex-col gap-6">
        <CourseSummaryCard course={course} />

        <div className="flex flex-col gap-3 self-center">
          <Button
            type="button"
            variant="danger"
            disabled={processing}
            onClick={cancel}
          >
            Anula tu inscripción
          </Button>

          {!waitlist && (
            <Button
              type="button"
              variant="secondary"
              disabled={processing}
              onClick={goToConfirm}
            >
              Confirma tu inscripción
            </Button>
          )}
        </div>
      </div>
    </AuthCard>
  );
}
